// ============================================================================
// Damage-refund API — the SO-line "Damage" action.
//
// Separate from returns.api.ts because the damage flow is a parallel, non-return
// money-only path (customer keeps the damaged goods; CN is ad-hoc, no stock).
// Shares the Refunds tab + bank-details endpoints via the backend's shared
// REFUND_FLOW params schema.
// ============================================================================
import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";

// Per-AllocationItem row the modal renders. `damageable_qty` is the shared-pool
// ceiling: `delivered − returns_booked − credit_returned − damage_refunded`.
export interface DamageContextItem {
  allocation_item_id: number;
  product_name: string;
  delivered_qty: string;
  damageable_qty: string;
  unit_rate: string;
  unit_rate_incl_tax?: string;
  rate_source?: "live_so" | "estimate_fallback";
}

export interface DamageContextResponse {
  sol_id: number;
  so_id: number;
  product_name: string | null;
  user_id: string | null;
  items: DamageContextItem[];
  totals: {
    damageable_qty: string;
    suggested_amount: string;
    suggested_amount_incl_tax?: string;
  };
  // Top-level flag — the SO has one tax regime, so the whole modal falls back together. Modal
  // shows one warning banner when "estimate_fallback" so the operator knows the prefill ignores
  // GST + order discount.
  rate_source?: "live_so" | "estimate_fallback";
}

export async function getDamageContext(
  solId: number
): Promise<DamageContextResponse | undefined> {
  try {
    return await axios
      .get(`/oms/damage/sol/${solId}/context/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading damage context");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// Submit shape. Rate per line is editable; qty must be ≤ damageable_qty server-side.
export interface DamageItem {
  allocation_item_id: number;
  qty: string | number;
  rate_incl_tax: string | number;
}

export interface FileDamageInput {
  items: DamageItem[];
  destination: "wallet" | "customer";
  bank_details_id?: number | null;
  note?: string;
  // UUID generated when the modal opens. Reposting with the same key is a no-op (returns the
  // same escalation) — guards against double-click + network retries creating duplicate CNs.
  idempotency_key: string;
}

export interface FileDamageResponse {
  damage_refund_escalation_id: number;
  amount: string;
  amount_incl_tax?: string;
  idempotent_replay: boolean;
}

export async function fileDamageRefund(
  solId: number,
  input: FileDamageInput
): Promise<FileDamageResponse | undefined> {
  try {
    return await axios
      .post(`/oms/damage/sol/${solId}/file/`, input)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "File damage refund failed");
        return undefined;
      });
  } catch {
    return undefined;
  }
}
